import {
  type CSSProperties,
  type FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import TruncatedTooltipText from "src/components/reusable/StyledTooltip/TruncatedTooltipText";

export const SelectMenu: FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (_value: string) => void;
  className?: string;
  dropdownClassName?: string;
  ariaLabel?: string;
  openUpward?: boolean;
  disabled?: boolean;
}> = ({ value, options, onChange, className = "", dropdownClassName = "", ariaLabel, openUpward = false, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const [scrollThumbStyle, setScrollThumbStyle] = useState<CSSProperties>({ opacity: 0 });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(option => option.value === value) ?? options[0] ?? { value: "", label: "" };
  const alwaysShowInventoryScrollbar = dropdownClassName.includes("sales-product-analytics-dashboard__select-dropdown--inventory-categories");

  const updateDropdownPosition = useCallback(() => {
    if (!rootRef.current) { return; }
    const rect = rootRef.current.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    if (rect.bottom < 0 || rect.top > viewportHeight) {
      setOpen(false);
      return;
    }

    const gap = 8;
    const maxDropdownHeight = 240;
    const style: CSSProperties = {
      position: "fixed",
      left: rect.left,
      width: Math.max(rect.width, 160),
      zIndex: 2000,
      overflow: "hidden",
    };

    if (openUpward) {
      const spaceAbove = rect.top - gap;
      const maxHeight = Math.min(maxDropdownHeight, Math.max(spaceAbove - gap, 120));
      style.maxHeight = maxHeight;
      style.top = Math.max(gap, rect.top - maxHeight - gap);
    } else {
      style.maxHeight = Math.min(maxDropdownHeight, viewportHeight - rect.bottom - gap);
      style.top = rect.bottom + gap;
    }

    setDropdownStyle(style);
  }, [openUpward]);

  const updateScrollThumb = useCallback(() => {
    if (!alwaysShowInventoryScrollbar) {
      setScrollThumbStyle({ opacity: 0 });
      return;
    }

    const viewport = scrollViewportRef.current;
    if (!viewport) { return; }

    const { clientHeight, scrollHeight, scrollTop } = viewport;
    if (scrollHeight <= clientHeight + 1 || clientHeight <= 0) {
      setScrollThumbStyle({ opacity: 0 });
      return;
    }

    const trackInset = 2;
    const trackHeight = Math.max(clientHeight - (trackInset * 2), 0);
    const thumbHeight = Math.max((clientHeight / scrollHeight) * trackHeight, 24);
    const maxThumbTravel = Math.max(trackHeight - thumbHeight, 0);
    const scrollRange = Math.max(scrollHeight - clientHeight, 1);
    const thumbTop = trackInset + ((scrollTop / scrollRange) * maxThumbTravel);

    setScrollThumbStyle({
      height: thumbHeight,
      transform: `translateY(${thumbTop}px)`,
      opacity: 1,
    });
  }, [alwaysShowInventoryScrollbar]);

  useLayoutEffect(() => {
    if (!open) { return; }
    updateDropdownPosition();
    updateScrollThumb();
    const frame = window.requestAnimationFrame(() => {
      updateDropdownPosition();
      updateScrollThumb();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, updateDropdownPosition, updateScrollThumb, options.length]);

  useEffect(() => {
    if (!open) { return; }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) {
        return;
      }
      if (
        !dropdownRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) { return; }
      updateDropdownPosition();
    };
    const handleViewportScroll = () => {
      updateScrollThumb();
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("resize", updateScrollThumb);
    const scrollViewport = scrollViewportRef.current;
    scrollViewport?.addEventListener("scroll", handleViewportScroll);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("resize", updateScrollThumb);
      scrollViewport?.removeEventListener("scroll", handleViewportScroll);
    };
  }, [open, updateDropdownPosition, updateScrollThumb]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    updateDropdownPosition();
    setOpen(true);
  };

  return (
    <div
      ref={rootRef}
      className={`sales-product-analytics-dashboard__select-menu ${className}`.trim()}
    >
      <button
        type="button"
        className={`sales-product-analytics-dashboard__select-trigger ${open ? "sales-product-analytics-dashboard__select-trigger--open" : ""}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <TruncatedTooltipText
          text={selected.label}
          className="sales-product-analytics-dashboard__select-trigger-label"
        >
          {selected.label}
        </TruncatedTooltipText>
        <ChevronDown className="sales-product-analytics-dashboard__select-trigger-icon" />
      </button>
      {open && typeof document !== "undefined" ? createPortal(
        <div
          ref={dropdownRef}
          className={`sales-product-analytics-dashboard__select-dropdown ${dropdownClassName}`.trim()}
          style={dropdownStyle}
          role="menu"
        >
          <div
            ref={scrollViewportRef}
            className={`sales-product-analytics-dashboard__select-dropdown-scroll ${alwaysShowInventoryScrollbar ? "sales-product-analytics-dashboard__select-dropdown-scroll--inventory-categories" : ""}`.trim()}
          >
            {options.map(option => {
              const active = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  className={`sales-product-analytics-dashboard__select-option ${active ? "sales-product-analytics-dashboard__select-option--active" : ""}`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <span className="sales-product-analytics-dashboard__select-option-check">
                    {active ? <Check size={16} /> : null}
                  </span>
                  <TruncatedTooltipText
                    text={option.label}
                    className="sales-product-analytics-dashboard__select-option-label"
                  >
                    {option.label}
                  </TruncatedTooltipText>
                </button>
              );
            })}
          </div>
          {alwaysShowInventoryScrollbar ? (
            <div className="sales-product-analytics-dashboard__select-dropdown-scrollbar" aria-hidden="true">
              <div
                className="sales-product-analytics-dashboard__select-dropdown-scrollbar-thumb"
                style={scrollThumbStyle}
              />
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </div>
  );
};

export default SelectMenu;
